/**
 * Which of the two question sections a question belongs to.
 *
 * SURPRISING AND LOAD-BEARING: the section is DERIVED from the pricing data, not declared. A
 * question whose answers can add a product belongs to section 3, "Add to your package"; a
 * question where no answer resolves to a product belongs to section 4, "Interested in growing
 * further?", where nothing is charged today.
 *
 * The consequence to know before editing product-config.mjs: attaching `addProduct` to any
 * answer of an SEO or ads question MOVES that question out of section 4 and into section 3, and
 * changes its rows from "No charge" to a price. Nothing warns you. This was chosen over an
 * explicit `group` field because a declared group can contradict the data -- a question marked
 * "interest" with a priced answer would render "No charge" next to a real charge, which is the
 * worse failure of the two.
 *
 * Both the section partition in DynamicForm and the row rendering in QuestionCard call this, so
 * they cannot drift apart on the answer.
 */
export function isPricedQuestion(question) {
  return question.answers.some((answer) => Boolean(answer.addProduct));
}

/**
 * Split a question list into the two sections, preserving config order within each.
 *
 * @returns {{ priced: Array, interest: Array }}
 */
export function partitionQuestions(questions) {
  const priced = [];
  const interest = [];

  questions.forEach((question) => {
    (isPricedQuestion(question) ? priced : interest).push(question);
  });

  return { priced, interest };
}

/**
 * "1 of 3 answered", for a section's right-aligned status slot.
 *
 * Derived on every render from the answers themselves. Storing a count would be a second
 * source of truth about state the reducer already owns.
 */
export function answeredCount(questions, questionAnswers) {
  return questions.filter((question) => {
    const value = questionAnswers[question.id];
    if (value == null) return false;
    // Checkbox answers are a {id: bool} map, and an all-false map is not an answer.
    if (typeof value === 'object') return Object.values(value).some(Boolean);
    return value !== '';
  }).length;
}
